import { useEffect, useState, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import api, { API_BASE } from "../lib/api";
import { fmtDate, ESTADO_COLOR, ESTADO_LABEL } from "../lib/utils";
import {
  Button, Input, Textarea, Select, FormField, Dialog,
  Badge, Spinner, Empty, PageHeader, SearchInput, SearchableSelect,
  Table, TableHeader, TableBody, TableRow, TableHead, TableCell,
} from "../components/ui";
import { Plus, Pencil, Camera, Trash2 } from "lucide-react";
import { toast } from "sonner";

const ESTADOS = [
  "INGRESADO","EN_PRESUPUESTO","APROBADO","EN_PROCESO",
  "CONTROL_CALIDAD","LISTO","ENTREGADO","CANCELADO",
];

const TIPOS = ["CHAPERIA","PINTURA","AMBOS","OTRO"];

function empty() {
  return {
    vehiculo_id: "", cliente_id: "", tipo_trabajo: "AMBOS",
    descripcion_danio: "", kilometraje: "", tecnico_id: "",
    fecha_estimada_entrega: "", aseguradora_id: "",
    numero_poliza: "", numero_siniestro: "", agente: "", numero_provision: "", observaciones: "",
    // datos nuevos vehículo / cliente para crear inline
    _nuevo_cliente: false, _cliente_nombre: "", _cliente_ruc: "", _cliente_tel: "",
    _marca: "", _modelo: "", _anio: "", _patente: "", _color: "", _vin_chasis: "",
  };
}

export default function Recepcion() {
  const [list,     setList]     = useState([]);
  const [clientes, setClientes] = useState([]);
  const [tecnicos, setTecnicos] = useState([]);
  const [aseguradoras, setAseg]  = useState([]);
  const [vehiculos,setVehiculos] = useState([]);
  const [loading,  setLoading]  = useState(true);
  const [search,   setSearch]   = useState("");
  const [estadoF,  setEstadoF]  = useState("");
  const [form,     setForm]     = useState(empty());
  const [open,     setOpen]     = useState(false);
  const [detail,   setDetail]   = useState(null);  // recepcion seleccionada para ver fotos
  const [saving,   setSaving]   = useState(false);

  const load = useCallback(async () => {
    const params = {};
    if (estadoF) params.estado = estadoF;
    const [r, c, t, a] = await Promise.all([
      api.get("/recepciones", { params }),
      api.get("/clientes"),
      api.get("/tecnicos"),
      api.get("/aseguradoras"),
    ]);
    setList(r.data);
    setClientes(c.data);
    setTecnicos(t.data);
    setAseg(a.data);
    setLoading(false);
  }, [estadoF]);

  useEffect(() => { load(); }, [load]);

  // cargar vehiculos del cliente seleccionado
  useEffect(() => {
    if (form.cliente_id && !form._nuevo_cliente) {
      api.get("/vehiculos", { params: { cliente_id: form.cliente_id } })
        .then((r) => setVehiculos(r.data));
    } else {
      setVehiculos([]);
    }
  }, [form.cliente_id, form._nuevo_cliente]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  async function handleSubmit(e) {
    e.preventDefault();
    setSaving(true);
    try {
      let cliente_id  = form.cliente_id;
      let vehiculo_id = form.vehiculo_id;

      // Crear cliente/vehículo si son nuevos
      if (form._nuevo_cliente) {
        const c = await api.post("/clientes", {
          nombre: form._cliente_nombre,
          ruc_ci: form._cliente_ruc || null,
          telefono: form._cliente_tel || null,
        });
        cliente_id = c.data.id;
      }
      if (!vehiculo_id) {
        const v = await api.post("/vehiculos", {
          cliente_id,
          marca: form._marca, modelo: form._modelo,
          anio: form._anio ? parseInt(form._anio) : null,
          patente: form._patente || null,
          color: form._color || null,
          vin_chasis: form._vin_chasis || null,
        });
        vehiculo_id = v.data.id;
      }

      await api.post("/recepciones", {
        vehiculo_id, cliente_id,
        tipo_trabajo: form.tipo_trabajo,
        descripcion_danio: form.descripcion_danio || null,
        kilometraje: form.kilometraje ? parseInt(form.kilometraje) : null,
        tecnico_id: form.tecnico_id || null,
        fecha_estimada_entrega: form.fecha_estimada_entrega || null,
        aseguradora_id: form.aseguradora_id || null,
        numero_poliza: form.numero_poliza || null,
        numero_siniestro: form.numero_siniestro || null,
        agente: form.agente || null,
        numero_provision: form.numero_provision || null,
        observaciones: form.observaciones || null,
      });
      toast.success("Recepción registrada");
      setOpen(false);
      setForm(empty());
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail ?? "Error al guardar");
    } finally {
      setSaving(false);
    }
  }

  const filtered = list.filter((r) => {
    if (!search) return true;
    const q = search.toLowerCase();
    return (
      r.vehiculo_desc?.toLowerCase().includes(q) ||
      r.cliente_nombre?.toLowerCase().includes(q) ||
      r.numero?.toLowerCase().includes(q)
    );
  });

  if (loading) return <div className="flex items-center justify-center h-full"><Spinner className="w-8 h-8" /></div>;

  return (
    <div className="p-4 sm:p-6">
      <PageHeader title="Recepción de Vehículos" sub={`${list.length} registros`}>
        <Button onClick={() => { setForm(empty()); setOpen(true); }}>
          <Plus className="w-4 h-4" /> Nueva recepción
        </Button>
      </PageHeader>

      {/* Filtros */}
      <div className="flex flex-wrap gap-3 mb-4">
        <SearchInput value={search} onChange={setSearch} placeholder="Buscar vehículo, cliente…" className="w-full sm:w-64" />
        <Select value={estadoF} onChange={(e) => setEstadoF(e.target.value)} className="w-full sm:w-48">
          <option value="">Todos los estados</option>
          {ESTADOS.map((s) => <option key={s}>{s}</option>)}
        </Select>
      </div>

      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>N°</TableHead>
            <TableHead>Fecha ingreso</TableHead>
            <TableHead>Vehículo</TableHead>
            <TableHead>Cliente</TableHead>
            <TableHead>Tipo</TableHead>
            <TableHead>Técnico</TableHead>
            <TableHead>Estado</TableHead>
            <TableHead></TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {filtered.length === 0 && (
            <TableRow>
              <TableCell colSpan={8}>
                <Empty message="No hay recepciones" />
              </TableCell>
            </TableRow>
          )}
          {filtered.map((r) => (
            <TableRow key={r.id}>
              <TableCell className="font-mono text-xs">{r.numero}</TableCell>
              <TableCell className="text-sm">{fmtDate(r.fecha_ingreso)}</TableCell>
              <TableCell>
                <p className="text-sm font-medium">{r.vehiculo_desc}</p>
              </TableCell>
              <TableCell className="text-sm">{r.cliente_nombre}</TableCell>
              <TableCell className="text-xs">{r.tipo_trabajo}</TableCell>
              <TableCell className="text-sm">{r.tecnico_nombre ?? <span className="text-muted-foreground">—</span>}</TableCell>
              <TableCell>
                <Badge className={ESTADO_COLOR[r.estado]}>
                  {ESTADO_LABEL[r.estado] ?? r.estado}
                </Badge>
              </TableCell>
              <TableCell>
                <button
                  onClick={() => setDetail(r)}
                  className="p-1.5 rounded hover:bg-secondary text-muted-foreground hover:text-foreground transition-colors"
                  title="Editar recepción"
                >
                  <Pencil className="w-4 h-4" />
                </button>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>

      {/* Dialog nueva recepción */}
      <Dialog open={open} onClose={() => setOpen(false)} title="Nueva Recepción" size="xl">
        <form onSubmit={handleSubmit} className="space-y-5">
          {/* Cliente */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm font-semibold">Cliente</span>
              <button
                type="button"
                onClick={() => set("_nuevo_cliente", !form._nuevo_cliente)}
                className="text-xs text-primary hover:underline"
              >
                {form._nuevo_cliente ? "← Seleccionar existente" : "+ Crear nuevo"}
              </button>
            </div>
            {form._nuevo_cliente ? (
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                <FormField label="Nombre *">
                  <Input value={form._cliente_nombre} onChange={(e) => set("_cliente_nombre", e.target.value)} required />
                </FormField>
                <FormField label="RUC/CI">
                  <Input value={form._cliente_ruc} onChange={(e) => set("_cliente_ruc", e.target.value)} />
                </FormField>
                <FormField label="Teléfono">
                  <Input value={form._cliente_tel} onChange={(e) => set("_cliente_tel", e.target.value)} />
                </FormField>
              </div>
            ) : (
              <FormField>
                <SearchableSelect
                  value={form.cliente_id}
                  onChange={(val) => set("cliente_id", val)}
                  placeholder="Seleccionar cliente…"
                  options={clientes.map((c) => ({
                    value:    c.id,
                    label:    c.nombre,
                    sublabel: c.ruc_ci || undefined,
                  }))}
                  required
                />
              </FormField>
            )}
          </div>

          {/* Vehículo */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm font-semibold">Vehículo</span>
            </div>
            {!form._nuevo_cliente && vehiculos.length > 0 ? (
              <div className="space-y-2">
                <FormField label="Seleccionar vehículo existente">
                  <Select value={form.vehiculo_id} onChange={(e) => set("vehiculo_id", e.target.value)}>
                    <option value="">+ Registrar nuevo vehículo</option>
                    {vehiculos.map((v) => (
                      <option key={v.id} value={v.id}>
                        {v.marca} {v.modelo} — {v.patente ?? "sin patente"}
                      </option>
                    ))}
                  </Select>
                </FormField>
              </div>
            ) : null}
            {(!form.vehiculo_id) && (
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mt-2">
                <FormField label="Marca *">
                  <Input value={form._marca} onChange={(e) => set("_marca", e.target.value)} required={!form.vehiculo_id} />
                </FormField>
                <FormField label="Modelo *">
                  <Input value={form._modelo} onChange={(e) => set("_modelo", e.target.value)} required={!form.vehiculo_id} />
                </FormField>
                <FormField label="Año">
                  <Input type="number" value={form._anio} onChange={(e) => set("_anio", e.target.value)} min={1950} max={2099} />
                </FormField>
                <FormField label="Patente">
                  <Input value={form._patente} onChange={(e) => set("_patente", e.target.value)} />
                </FormField>
                <FormField label="Color">
                  <Input value={form._color} onChange={(e) => set("_color", e.target.value)} />
                </FormField>
                <FormField label="Chasis">
                  <Input value={form._vin_chasis} onChange={(e) => set("_vin_chasis", e.target.value)} />
                </FormField>
              </div>
            )}
          </div>

          {/* Datos de la recepción */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <FormField label="Tipo de trabajo">
              <Select value={form.tipo_trabajo} onChange={(e) => set("tipo_trabajo", e.target.value)}>
                {TIPOS.map((t) => <option key={t}>{t}</option>)}
              </Select>
            </FormField>
            <FormField label="Kilometraje">
              <Input type="number" value={form.kilometraje} onChange={(e) => set("kilometraje", e.target.value)} min={0} />
            </FormField>
            <FormField label="Técnico responsable">
              <Select value={form.tecnico_id} onChange={(e) => set("tecnico_id", e.target.value)}>
                <option value="">Sin asignar</option>
                {tecnicos.map((t) => <option key={t.id} value={t.id}>{t.nombre}</option>)}
              </Select>
            </FormField>
            <FormField label="Fecha estimada entrega">
              <Input type="datetime-local" value={form.fecha_estimada_entrega} onChange={(e) => set("fecha_estimada_entrega", e.target.value)} />
            </FormField>
          </div>

          <FormField label="Descripción del daño">
            <Textarea
              rows={3}
              value={form.descripcion_danio}
              onChange={(e) => set("descripcion_danio", e.target.value)}
              placeholder="Descripción detallada del daño…"
            />
          </FormField>

          {/* Seguro */}
          <details className="group">
            <summary className="text-sm font-medium cursor-pointer text-muted-foreground hover:text-foreground select-none">
              Datos de seguro (opcional)
            </summary>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-3">
              <FormField label="Aseguradora">
                <Select value={form.aseguradora_id} onChange={(e) => set("aseguradora_id", e.target.value)}>
                  <option value="">Sin seguro</option>
                  {aseguradoras.map((a) => <option key={a.id} value={a.id}>{a.nombre}</option>)}
                </Select>
              </FormField>
              <FormField label="N° Póliza">
                <Input value={form.numero_poliza} onChange={(e) => set("numero_poliza", e.target.value)} />
              </FormField>
              <FormField label="N° Siniestro">
                <Input value={form.numero_siniestro} onChange={(e) => set("numero_siniestro", e.target.value)} />
              </FormField>
              <FormField label="Agente">
                <Input value={form.agente} onChange={(e) => set("agente", e.target.value)} />
              </FormField>
              <FormField label="N° Orden de Provisión">
                <Input value={form.numero_provision} onChange={(e) => set("numero_provision", e.target.value)} placeholder="Ej: 47530" />
              </FormField>
            </div>
          </details>

          <FormField label="Observaciones">
            <Textarea rows={2} value={form.observaciones} onChange={(e) => set("observaciones", e.target.value)} />
          </FormField>

          <div className="flex justify-end gap-2 pt-2">
            <Button type="button" variant="outline" onClick={() => setOpen(false)}>Cancelar</Button>
            <Button type="submit" loading={saving}>Registrar recepción</Button>
          </div>
        </form>
      </Dialog>

      {/* Detail sheet */}
      {detail && (
        <RecepcionDetail
          recepcion={detail}
          tecnicos={tecnicos}
          aseguradoras={aseguradoras}
          clientes={clientes}
          onUpdate={(updated) => {
            setList((l) => l.map((r) => (r.id === updated.id ? updated : r)));
            setDetail(updated);
          }}
          onDelete={(id) => {
            setList((l) => l.filter((r) => r.id !== id));
            setDetail(null);
          }}
          onClose={() => setDetail(null)}
        />
      )}
    </div>
  );
}

function RecepcionDetail({ recepcion, tecnicos, aseguradoras, clientes, onUpdate, onDelete, onClose }) {
  const navigate = useNavigate();
  const [fotos, setFotos]       = useState([]);
  const [uploading, setUpl]     = useState(false);
  const [saving, setSaving]     = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [caption, setCaption]   = useState("");
  const fileRef = useRef(null);

  const [editForm, setEditForm] = useState({
    // recepcion fields
    cliente_id:             recepcion.cliente_id ?? "",
    tipo_trabajo:           recepcion.tipo_trabajo ?? "AMBOS",
    descripcion_danio:      recepcion.descripcion_danio ?? "",
    kilometraje:            recepcion.kilometraje ?? "",
    tecnico_id:             recepcion.tecnico_id ?? "",
    fecha_estimada_entrega: recepcion.fecha_estimada_entrega
      ? recepcion.fecha_estimada_entrega.slice(0, 16) : "",
    aseguradora_id:   recepcion.aseguradora_id ?? "",
    numero_poliza:    recepcion.numero_poliza ?? "",
    numero_siniestro: recepcion.numero_siniestro ?? "",
    agente:           recepcion.agente ?? "",
    numero_provision: recepcion.numero_provision ?? "",
    estado:           recepcion.estado ?? "INGRESADO",
    observaciones:    recepcion.observaciones ?? "",
    // vehicle fields
    _marca:      recepcion.vehiculo_marca ?? "",
    _modelo:     recepcion.vehiculo_modelo ?? "",
    _anio:       recepcion.vehiculo_anio ?? "",
    _patente:    recepcion.vehiculo_patente ?? "",
    _color:      recepcion.vehiculo_color ?? "",
    _vin_chasis: recepcion.vehiculo_vin_chasis ?? "",
  });

  function setE(k, v) { setEditForm((f) => ({ ...f, [k]: v })); }

  useEffect(() => {
    api.get("/fotos", { params: { entidad: "recepcion", entidad_id: recepcion.id } })
      .then((r) => setFotos(r.data));
  }, [recepcion.id]);

  async function handleSave() {
    setSaving(true);
    try {
      // Update vehicle data
      await api.put(`/vehiculos/${recepcion.vehiculo_id}`, {
        marca:      editForm._marca || null,
        modelo:     editForm._modelo || null,
        anio:       editForm._anio ? parseInt(editForm._anio) : null,
        patente:    editForm._patente || null,
        color:      editForm._color || null,
        vin_chasis: editForm._vin_chasis || null,
      });
      // Update recepcion
      const payload = {
        cliente_id:             editForm.cliente_id ? parseInt(editForm.cliente_id) : null,
        tipo_trabajo:           editForm.tipo_trabajo || null,
        descripcion_danio:      editForm.descripcion_danio || null,
        kilometraje:            editForm.kilometraje ? parseInt(editForm.kilometraje) : null,
        tecnico_id:             editForm.tecnico_id ? parseInt(editForm.tecnico_id) : null,
        fecha_estimada_entrega: editForm.fecha_estimada_entrega || null,
        aseguradora_id:         editForm.aseguradora_id ? parseInt(editForm.aseguradora_id) : null,
        numero_poliza:          editForm.numero_poliza || null,
        numero_siniestro:       editForm.numero_siniestro || null,
        agente:                 editForm.agente || null,
        numero_provision:       editForm.numero_provision || null,
        estado:                 editForm.estado || null,
        observaciones:          editForm.observaciones || null,
      };
      const r = await api.put(`/recepciones/${recepcion.id}`, payload);
      toast.success("Recepción actualizada");
      onUpdate(r.data);
    } catch (err) {
      toast.error(err.response?.data?.detail ?? "Error al guardar");
    } finally {
      setSaving(false);
    }
  }

  async function handleDeleteRecepcion() {
    if (!window.confirm("¿Eliminar esta recepción? Esta acción no se puede deshacer.")) return;
    setDeleting(true);
    try {
      await api.delete(`/recepciones/${recepcion.id}`);
      toast.success("Recepción eliminada");
      onDelete(recepcion.id);
    } catch (err) {
      toast.error(err.response?.data?.detail ?? "Error al eliminar");
    } finally {
      setDeleting(false);
    }
  }

  async function handleUpload(e) {
    const file = e.target.files?.[0];
    if (!file) return;
    setUpl(true);
    const fd = new FormData();
    fd.append("file", file);
    fd.append("entidad", "recepcion");
    fd.append("entidad_id", recepcion.id);
    if (caption.trim()) fd.append("descripcion", caption.trim());
    try {
      const r = await api.post("/fotos", fd);
      setFotos((f) => [...f, r.data]);
      setCaption("");
      toast.success("Foto subida");
    } catch (err) {
      toast.error(err?.response?.data?.detail ?? "Error al subir foto");
    } finally {
      setUpl(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  }

  async function handleDelete(id) {
    await api.delete(`/fotos/${id}`);
    setFotos((f) => f.filter((x) => x.id !== id));
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-end">
      <div className="absolute inset-0 bg-black/40" onClick={onClose} />
      <div className="relative z-10 h-full w-full sm:w-[540px] bg-card border-l border-border flex flex-col shadow-xl overflow-y-auto">
        <div className="flex items-center justify-between p-5 border-b border-border">
          <h2 className="font-semibold font-display truncate min-w-0 mr-2">{recepcion.numero} — {recepcion.vehiculo_desc}</h2>
          <button onClick={onClose} className="p-1.5 rounded hover:bg-secondary text-muted-foreground">✕</button>
        </div>
        <div className="p-5 space-y-5">
          {/* Campos editables */}
          <div className="space-y-3">
            {/* Cliente */}
            <FormField label="Cliente">
              <SearchableSelect
                value={editForm.cliente_id}
                onChange={(val) => setE("cliente_id", val)}
                placeholder="Seleccionar cliente…"
                options={clientes.map((c) => ({
                  value:    c.id,
                  label:    c.nombre,
                  sublabel: c.ruc_ci || undefined,
                }))}
              />
            </FormField>

            {/* Vehículo */}
            <details open>
              <summary className="text-sm font-semibold cursor-pointer select-none mb-2">Vehículo</summary>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mt-2">
                <FormField label="Marca">
                  <Input value={editForm._marca} onChange={(e) => setE("_marca", e.target.value)} />
                </FormField>
                <FormField label="Modelo">
                  <Input value={editForm._modelo} onChange={(e) => setE("_modelo", e.target.value)} />
                </FormField>
                <FormField label="Año">
                  <Input type="number" value={editForm._anio} onChange={(e) => setE("_anio", e.target.value)} min={1950} max={2099} />
                </FormField>
                <FormField label="Patente">
                  <Input value={editForm._patente} onChange={(e) => setE("_patente", e.target.value)} />
                </FormField>
                <FormField label="Color">
                  <Input value={editForm._color} onChange={(e) => setE("_color", e.target.value)} />
                </FormField>
                <FormField label="Chasis">
                  <Input value={editForm._vin_chasis} onChange={(e) => setE("_vin_chasis", e.target.value)} />
                </FormField>
              </div>
            </details>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <FormField label="Estado">
                <Select value={editForm.estado} onChange={(e) => setE("estado", e.target.value)}>
                  {ESTADOS.map((s) => <option key={s}>{s}</option>)}
                </Select>
              </FormField>
              <FormField label="Tipo de trabajo">
                <Select value={editForm.tipo_trabajo} onChange={(e) => setE("tipo_trabajo", e.target.value)}>
                  {TIPOS.map((t) => <option key={t}>{t}</option>)}
                </Select>
              </FormField>
              <FormField label="Técnico">
                <Select value={editForm.tecnico_id} onChange={(e) => setE("tecnico_id", e.target.value)}>
                  <option value="">Sin asignar</option>
                  {tecnicos.map((t) => <option key={t.id} value={t.id}>{t.nombre}</option>)}
                </Select>
              </FormField>
              <FormField label="Kilometraje">
                <Input type="number" value={editForm.kilometraje} onChange={(e) => setE("kilometraje", e.target.value)} min={0} />
              </FormField>
            </div>
            <FormField label="Fecha estimada de entrega">
              <Input type="datetime-local" value={editForm.fecha_estimada_entrega} onChange={(e) => setE("fecha_estimada_entrega", e.target.value)} />
            </FormField>
            <FormField label="Descripción del daño">
              <Textarea rows={2} value={editForm.descripcion_danio} onChange={(e) => setE("descripcion_danio", e.target.value)} placeholder="Descripción detallada del daño…" />
            </FormField>
            <details>
              <summary className="text-xs text-muted-foreground cursor-pointer hover:text-foreground select-none">Datos de seguro (opcional)</summary>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-2">
                <FormField label="Aseguradora">
                  <Select value={editForm.aseguradora_id} onChange={(e) => setE("aseguradora_id", e.target.value)}>
                    <option value="">Sin seguro</option>
                    {aseguradoras.map((a) => <option key={a.id} value={a.id}>{a.nombre}</option>)}
                  </Select>
                </FormField>
                <FormField label="N° Póliza">
                  <Input value={editForm.numero_poliza} onChange={(e) => setE("numero_poliza", e.target.value)} />
                </FormField>
                <FormField label="N° Siniestro">
                  <Input value={editForm.numero_siniestro} onChange={(e) => setE("numero_siniestro", e.target.value)} />
                </FormField>
                <FormField label="Agente">
                  <Input value={editForm.agente} onChange={(e) => setE("agente", e.target.value)} />
                </FormField>
                <FormField label="N° Orden de Provisión">
                  <Input value={editForm.numero_provision} onChange={(e) => setE("numero_provision", e.target.value)} placeholder="Ej: 47530" />
                </FormField>
              </div>
            </details>
            <FormField label="Observaciones">
              <Textarea rows={2} value={editForm.observaciones} onChange={(e) => setE("observaciones", e.target.value)} />
            </FormField>
            <div className="flex justify-between">
              <div className="flex gap-2">
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => { onClose(); navigate("/presupuestos", { state: { recepcion_id: recepcion.id } }); }}
                >
                  <Plus className="w-3.5 h-3.5 mr-1" /> Crear presupuesto
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  loading={deleting}
                  onClick={handleDeleteRecepcion}
                  className="text-destructive border-destructive/40 hover:bg-destructive/10"
                >
                  <Trash2 className="w-3.5 h-3.5 mr-1" /> Eliminar
                </Button>
              </div>
              <Button onClick={handleSave} loading={saving} size="sm">Guardar cambios</Button>
            </div>
          </div>

          {/* Fotos */}
          <div>
            <p className="font-medium text-sm mb-2">Fotos del vehículo</p>
            <div className="flex gap-2 mb-3">
              <Input
                value={caption}
                onChange={(e) => setCaption(e.target.value)}
                placeholder="Caption opcional (pieza, daño…)"
                className="flex-1 text-sm"
              />
              <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={handleUpload} />
              <Button variant="outline" size="sm" disabled={uploading} onClick={() => fileRef.current?.click()}>
                <Camera className="w-3.5 h-3.5 mr-1" /> {uploading ? "Subiendo..." : "Subir foto"}
              </Button>
            </div>
            {fotos.length === 0 ? (
              <Empty message="Sin fotos aún" icon={Camera} />
            ) : (
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                {fotos.map((f) => (
                  <div key={f.id} className="relative group rounded-md overflow-hidden border border-border">
                    <div className="aspect-square">
                      <img
                        src={`${API_BASE}${f.url}`}
                        alt={f.descripcion ?? "foto"}
                        className="w-full h-full object-cover"
                      />
                    </div>
                    {f.descripcion && (
                      <p className="text-xs text-muted-foreground px-1.5 py-1 leading-tight bg-card border-t border-border truncate" title={f.descripcion}>{f.descripcion}</p>
                    )}
                    <button
                      onClick={() => handleDelete(f.id)}
                      className="absolute top-1 right-1 bg-black/60 text-white rounded p-0.5 opacity-0 group-hover:opacity-100 transition-opacity text-xs"
                    >✕</button>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
